/**
 * 统一消息提示工具
 * 基于Element UI的Message组件
 * 使用红色主题设计
 */

// 导入Element UI的Message组件
import { Message, MessageBox, Notification } from "element-ui";

/**
 * 消息提示类型
 */
const MESSAGE_TYPES = {
  SUCCESS: "success",
  WARNING: "warning",
  INFO: "info",
  ERROR: "error",
};

/**
 * 默认配置
 */
const DEFAULT_CONFIG = {
  duration: 3000,
  showClose: true,
  dangerouslyUseHTMLString: false, // 安全考虑，默认不允许HTML
};

/**
 * 获取自定义类名
 * @param {string} type - 消息类型
 * @param {boolean} useCustomTheme - 是否使用自定义主题
 */
const getCustomClass = (type, useCustomTheme = true) => {
  if (!useCustomTheme) return "";

  const baseClass = "custom-message-theme";
  const typeClass = `custom-message-${type}`;
  return `${baseClass} ${typeClass}`;
};

/**
 * 统一消息提示工具类
 */
class MessageUtil {
  /**
   * 成功提示
   * @param {string} message - 提示内容
   * @param {Object} options - 配置选项
   */
  static success(message, options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return Message({
      type: MESSAGE_TYPES.SUCCESS,
      message,
      ...DEFAULT_CONFIG,
      customClass: getCustomClass(MESSAGE_TYPES.SUCCESS, useCustomTheme),
      ...restOptions,
    });
  }

  /**
   * 警告提示
   * @param {string} message - 提示内容
   * @param {Object} options - 配置选项
   */
  static warning(message, options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return Message({
      type: MESSAGE_TYPES.WARNING,
      message,
      ...DEFAULT_CONFIG,
      customClass: getCustomClass(MESSAGE_TYPES.WARNING, useCustomTheme),
      ...restOptions,
    });
  }

  /**
   * 信息提示
   * @param {string} message - 提示内容
   * @param {Object} options - 配置选项
   */
  static info(message, options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return Message({
      type: MESSAGE_TYPES.INFO,
      message,
      ...DEFAULT_CONFIG,
      customClass: getCustomClass(MESSAGE_TYPES.INFO, useCustomTheme),
      ...restOptions,
    });
  }

  /**
   * 错误提示
   * @param {string} message - 提示内容
   * @param {Object} options - 配置选项
   */
  static error(message, options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return Message({
      type: MESSAGE_TYPES.ERROR,
      message,
      duration: 4000,
      showClose: true,
      customClass: getCustomClass(MESSAGE_TYPES.ERROR, useCustomTheme),
      ...restOptions,
    });
  }

  /**
   * 加载中提示
   * @param {string} message - 提示内容
   * @param {number} duration - 持续时间，0表示不自动关闭
   * @param {Object} options - 配置选项
   * @returns {Function} 关闭方法
   */
  static loading(message = "加载中...", duration = 0, options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    const instance = Message({
      type: MESSAGE_TYPES.INFO,
      message,
      duration,
      showClose: false,
      iconClass: "el-icon-loading",
      customClass: getCustomClass(MESSAGE_TYPES.INFO, useCustomTheme),
      ...restOptions,
    });

    // 返回关闭方法
    return () => {
      if (instance && typeof instance.close === "function") {
        instance.close();
      }
    };
  }

  /**
   * 确认对话框
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {Object} options - 配置选项
   */
  static confirm(message, title = "提示", options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return MessageBox.confirm(message, title, {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: MESSAGE_TYPES.WARNING,
      customClass: useCustomTheme ? "custom-msgbox-theme" : "",
      ...restOptions,
    });
  }

  /**
   * 警告对话框
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {Object} options - 配置选项
   */
  static alert(message, title = "提示", options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return MessageBox.alert(message, title, {
      confirmButtonText: "确定",
      type: MESSAGE_TYPES.INFO,
      customClass: useCustomTheme ? "custom-msgbox-theme" : "",
      ...restOptions,
    });
  }

  /**
   * 输入对话框
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {Object} options - 配置选项
   */
  static prompt(message, title = "输入", options = {}) {
    const { useCustomTheme = true, ...restOptions } = options;

    return MessageBox.prompt(message, title, {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      inputPattern: /\S/,
      inputErrorMessage: "输入不能为空",
      customClass: useCustomTheme ? "custom-msgbox-theme" : "",
      ...restOptions,
    });
  }

  /**
   * 通知提示
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {string} type - 类型
   * @param {Object} options - 配置选项
   */
  static notify(
    message,
    title = "通知",
    type = MESSAGE_TYPES.INFO,
    options = {}
  ) {
    const { useCustomTheme = true, ...restOptions } = options;

    return Notification({
      title,
      message,
      type,
      duration: 4500,
      position: "top-right",
      customClass: useCustomTheme ? "custom-notification-theme" : "",
      ...restOptions,
    });
  }

  /**
   * 成功通知
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {Object} options - 配置选项
   */
  static notifySuccess(message, title = "成功", options = {}) {
    return this.notify(message, title, MESSAGE_TYPES.SUCCESS, options);
  }

  /**
   * 警告通知
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {Object} options - 配置选项
   */
  static notifyWarning(message, title = "警告", options = {}) {
    return this.notify(message, title, MESSAGE_TYPES.WARNING, options);
  }

  /**
   * 错误通知
   * @param {string} message - 提示内容
   * @param {string} title - 标题
   * @param {Object} options - 配置选项
   */
  static notifyError(message, title = "错误", options = {}) {
    return this.notify(message, title, MESSAGE_TYPES.ERROR, options);
  }

  /**
   * 关闭所有消息提示
   */
  static closeAll() {
    Message.closeAll();
  }
}

// Vue插件安装方法
MessageUtil.install = function (Vue) {
  // 将MessageUtil挂载到Vue原型上
  Vue.prototype.$msg = MessageUtil;
};

export default MessageUtil;
export { MESSAGE_TYPES };
